import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { ArrowForwardOutlined, MoreHorizOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getActivationProgress, updateActivationProgress } from '../../services/api';

export default function ActivationBanner({ userId, enabled, visitKey }) {
  const navigate = useNavigate();
  const [recommendation, setRecommendation] = useState(null);
  const [anchor, setAnchor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const generation = useRef(0);
  const refresh = useCallback(async () => {
    const request = ++generation.current;
    try {
      const result = await getActivationProgress();
      if (request === generation.current) setRecommendation(result.recommendation || null);
    } catch { if (request === generation.current) setRecommendation(null); }
  }, []);
  useEffect(() => {
    setRecommendation(null); setError(''); setAnchor(null);
  }, [enabled, userId]);
  useEffect(() => {
    if (!enabled || !userId) return undefined;
    refresh();
    window.addEventListener('promptcrew:activation-changed', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      generation.current += 1;
      window.removeEventListener('promptcrew:activation-changed', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [enabled, userId, visitKey, refresh]);
  if (!enabled || !recommendation) return null;
  const change = async (action) => {
    setAnchor(null); setBusy(true); setError('');
    try { await updateActivationProgress(recommendation.id, action); await refresh(); }
    catch { setError('Could not update this recommendation. Please try again.'); }
    finally { setBusy(false); }
  };
  return (
    <Box component="section" aria-label="Suggested next step" sx={{ mx: { xs: 1.5, sm: 2 }, mb: 1.5, px: 2, py: 1, pr: { xs: 7, md: 2 }, bgcolor: '#F0F7F5', borderLeft: '3px solid #34806B', borderBottom: '1px solid #DCEBE5' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.25 }}>
        <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, overflowWrap: 'anywhere' }}>{recommendation.title}</Typography>
          <Typography variant="body2" color="text.secondary">{recommendation.reason}</Typography>
        </Box>
        <Button disabled={busy} size="small" endIcon={<ArrowForwardOutlined />} onClick={() => navigate(recommendation.action.path)}>{recommendation.action.label}</Button>
        <Tooltip title="Recommendation options"><IconButton size="small" disabled={busy} aria-label="Recommendation options" onClick={(event) => setAnchor(event.currentTarget)}><MoreHorizOutlined /></IconButton></Tooltip>
        <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
          <MenuItem onClick={() => change('snooze')}>Remind me in a week</MenuItem>
          <MenuItem onClick={() => change('dismiss')}>Dismiss this suggestion</MenuItem>
        </Menu>
      </Box>
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    </Box>
  );
}
